import { useEffect, useRef, useState, useCallback } from "react";
import { MessagesSquare } from "lucide-react";
import UserMessage from "./UserMessage";
import AIMessage from "./AIMessage";

interface TranscriptMessage {
  id: string;
  type: "user" | "ai";
  content: string;
  pdf_page: number;
  timestamp: string;
  streaming?: boolean;
  isLoading?: boolean;
}

interface ChatThreadProps {
  transcript?: TranscriptMessage[];
  onSendMessage: (message: string) => void;
  sessionName?: string;
  topicId?: string;
  userName?: string;
}

const ChatThread = ({
  transcript = [],
  onSendMessage,
  sessionName = "Chat",
  topicId,
  userName
}: ChatThreadProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [scrollTimeout, setScrollTimeout] = useState<NodeJS.Timeout | null>(null);

  // Extract first name from full name
  const getFirstName = (fullName?: string) => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };
  
  const firstName = getFirstName(userName);
  
  const isAtBottom = useCallback(() => {
    if (!scrollContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    return scrollTop + clientHeight >= scrollHeight - 5;
  }, []);
  
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  };
  
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    if (!isAtBottom()) {
      setIsUserScrolling(true);
    } else {
      setIsUserScrolling(false);
    }
    if (scrollTimeout) clearTimeout(scrollTimeout);
    const timeout = setTimeout(() => {
      if (isAtBottom()) setIsUserScrolling(false);
    }, 150);
    setScrollTimeout(timeout);
  }, [isAtBottom, scrollTimeout]);

  // Auto-scroll when transcript changes
  useEffect(() => {
    if (transcript.length > 0 && !isUserScrolling) {
      setTimeout(scrollToBottom, 100);
    }
  }, [transcript.length, isUserScrolling]);

  // Initial scroll to bottom when component mounts
  useEffect(() => {
    if (transcript.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [scrollTimeout]);

  // Empty state when no transcript
  if (transcript.length === 0) {
    return (
      <div className="h-full flex flex-col pb-32 pt-6 px-4" style={{ fontFamily: 'Lexend Deca' }}>
        <div className="flex-1 flex flex-col justify-center items-center text-center space-y-8">
          {/* Welcome icon */}
          <MessagesSquare className="w-32 h-32 mx-auto text-muted-foreground" strokeWidth={1.5} />
          
          {/* Title */}
          <h2 className="text-2xl font-heading font-semibold text-[#171717]">
            Hey {firstName ? firstName : 'there'}, ready to learn?
          </h2>
          
          {/* Description */}
          <p className="text-gray-600 max-w-lg mx-auto text-base font-normal">
            Choose a learning mode below to get started, or ask a quick question!
          </p>
        </div>
      </div>
    );
  }

  // Chat thread with messages
  return (
    <div className="h-full flex flex-col" style={{ fontFamily: 'Lexend Deca' }}>
      {/* Chat Messages - Scrollable area */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto pr-2" onScroll={handleScroll}>
        <div className="space-y-4">
          {transcript.map((message, idx) => {
            const isLast = idx === transcript.length - 1;
            const isStreaming = isLast && message.type === 'ai' && (
              !!message.streaming ||
              !message.content || 
              message.content.length === 0
            );
            
            return message.type === 'user' 
              ? <UserMessage key={message.id} message={message.content} /> 
              : <AIMessage key={message.id} message={message.content} timestamp={message.timestamp} isStreaming={!!isStreaming} />;
          })}
        </div>
      </div>
    </div>
  );
};

export default ChatThread;
